/**
 * Контент и баланс v1 (E2, ISSUE-5): JSON-файлы этой папки, собранные в {@link BalanceData} и проверенные схемой.
 * Обзор для человека — `docs/context/content-v1.md`. Любая правка чисел — новая {@link BALANCE_VERSION} (MADR 0004):
 * ветка кладёт запись в `balance.d/`, версию и отпечаток выставляет шаг мержа.
 */
import { BalanceData } from "@newfungame/contracts";
import boosts from "./boosts.json";
import bosses from "./bosses.json";
import dailyTasks from "./daily-tasks.json";
import economy from "./economy.json";
import friendReward from "./friend-reward.json";
import heroes from "./heroes.json";
import hq from "./hq.json";
import league from "./league.json";
import parcels from "./parcels.json";
import season from "./season.json";
import skills from "./skills.json";
import streak from "./streak.json";
import tech from "./tech.json";
import waves from "./waves.json";
import zombies from "./zombies.json";
import base from "./previous/2026-10-27.1.json";
import beforeLateWaves from "./previous/2026-10-28.1.json";
import beforeEarlyWaves from "./previous/2026-11-01.2.json";
import beforeKeptHeroes from "./previous/2026-11-01.3.json";
import beforeRewardBrake from "./previous/2026-11-01.6.json";
import outgoing from "./outgoing.json";
import stamp from "./version.json";

/** Версия набора данных баланса (`YYYY-MM-DD.N`), попадает в журнал волны. Выставляет `scripts/premerge.mjs`. */
export const BALANCE_VERSION = stamp.version;

/** Сырые разделы как лежат в JSON (до проверки схемой) — для тестов и инструментов баланса. */
export const rawBalance = {
  version: BALANCE_VERSION,
  heroes,
  skills,
  boosts,
  zombies,
  bosses,
  tech,
  hq,
  waves,
  streak,
  economy,
  dailyTasks,
  season,
  league,
  friendReward,
  parcels,
};

/** Собирает и проверяет данные; бросает `ZodError`, если JSON не проходит схему или ссылки разорваны. */
export function loadBalance(): BalanceData {
  return BalanceData.parse(rawBalance);
}

/** Баланс v1, проверенный при загрузке модуля. */
export const balanceV1: BalanceData = loadBalance();

/**
 * Уходящая версия баланса: снимок `outgoing.json`, его обновляет шаг мержа. Сервер
 * держит её в реестре версий на время выката (`apps/server/src/waves/balances.ts`): клиент старой сборки доигрывает
 * волну на тех данных, с которыми играл. Убрать — когда выкат новой сборки завершён и старых клиентов не осталось.
 */
export const balanceOutgoing: BalanceData = BalanceData.parse(outgoing);

/** Числа `2026-11-01.6` (до тормоза награды ISSUE-69): с ними сверяют длинные забежки и награду тесты. В игре не используется. */
export const balanceBeforeRewardBrake: BalanceData = BalanceData.parse(beforeRewardBrake);

/** Числа `2026-11-01.3` (откат снимал героя, ISSUE-60): журнал без `keepOpenedHeroes` проверяют тесты. В игре не используется. */
export const balanceBeforeKeptHeroes: BalanceData = BalanceData.parse(beforeKeptHeroes);

/** Числа `2026-11-01.2` (до мягких первых волн ISSUE-47): с ними сверяет проверку волн 1–3 `first-waves.test.ts`. */
export const balanceBeforeEarlyWaves: BalanceData = BalanceData.parse(beforeEarlyWaves);

/**
 * Числа `2026-10-28.1` (до продлённой прокачки ISSUE-40): с ними сравнивают поздние волны тесты и симулятор
 * («игрок с максимумом прошлой версии»). В игре и в реестре сервера не используется.
 */
export const balanceBeforeLateWaves: BalanceData = BalanceData.parse(beforeLateWaves);

/**
 * Замороженные числа `2026-10-27.1` (до подгонки E6): стабильная основа юнит-тестов экономики, механик и интерфейса
 * (цены, доходы, серия) — им нужны фиксированные числа, а не текущий баланс. В игре не используется.
 */
export const balancePrevious: BalanceData = BalanceData.parse(base);
